import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  UtensilsCrossed, Plus, Search, Tag, FileDown, FileSpreadsheet
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { MenuProdottoDialog, MenuComponenteDialog, MenuCategoriaDialog } from "../../components/menu/MenuDialogs";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent } from "../../components/ui/card";
import { useConfirm } from "../../hooks/useConfirm";
import MenuStatsCards from "../../components/menu/MenuStatsCards";
import MenuTop3Card from "../../components/menu/MenuTop3Card";
import MenuFilters from "../../components/menu/MenuFilters";
import MenuProdottoCard from "../../components/menu/MenuProdottoCard";
import { useCostAlertMaps } from "../../components/CostAlerts";
import StoricoVariazioniDialog from "../../components/StoricoVariazioniDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper per formattare i prezzi
// Helpers (formatEuro, formatPercentuale, MargineBadge, crownRank) sono ora in menuHelpers.js

const Menu = () => {
  const { getAuthHeaders, localeCorrente } = useAuth();
  const confirm = useConfirm();
  const [loading, setLoading] = useState(true);
  const [exportingFoodCost, setExportingFoodCost] = useState(false);
  const [exportingIngredients, setExportingIngredients] = useState(false);
  const { piattiById: alertMap } = useCostAlertMaps(localeCorrente?.id);
  const [storicoItem, setStoricoItem] = useState(null);
  
  // Dati
  const [prodotti, setProdotti] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [ingredienti, setIngredienti] = useState([]);
  const [categorieIngredienti, setCategorieIngredienti] = useState([]);
  const [ricette, setRicette] = useState([]);
  const [materiali, setMateriali] = useState([]); // Per il packaging
  
  // Filtri
  const [search, setSearch] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState("tutte");
  const [filtroStato, setFiltroStato] = useState("tutti");
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Dialog categoria
  const [categoriaDialogOpen, setCategoriaDialogOpen] = useState(false);
  const [nuovaCategoria, setNuovaCategoria] = useState({ nome: "", icona: "🍽️", colore: "#6C63FF" });
  
  // Form prodotto
  const [form, setForm] = useState({
    nome: "",
    categoria_id: "",
    descrizione: "",
    prezzo_vendita: "",
    componenti: [],
    attivo: true
  });
  
  // Dialog aggiungi componente
  const [addComponentOpen, setAddComponentOpen] = useState(false);
  const [tipoComponente, setTipoComponente] = useState("ingrediente"); // "ingrediente" | "ricetta" | "packaging"
  const [nuovoComponente, setNuovoComponente] = useState({
    ingrediente_id: "",
    ricetta_id: "",
    packaging_id: "",
    quantita: ""
  });

  // Fetch dati
  const fetchData = useCallback(async () => {
    if (!localeCorrente?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    
    try {
      const headers = getAuthHeaders();
      const localeParam = `locale_id=${localeCorrente.id}`;
      
      const [prodRes, catRes, ingRes, catIngRes, ricRes, matRes] = await Promise.all([
        axios.get(`${API}/menu/prodotti?${localeParam}`, { headers }),
        axios.get(`${API}/menu/categorie?${localeParam}`, { headers }),
        axios.get(`${API}/ingredienti?${localeParam}`, { headers }),
        axios.get(`${API}/categorie-ingredienti?${localeParam}`, { headers }),
        axios.get(`${API}/ricette?${localeParam}`, { headers }),
        axios.get(`${API}/materiali?${localeParam}`, { headers })
      ]);
      
      setProdotti(prodRes.data || []);
      setCategorie(catRes.data || []);
      setIngredienti(ingRes.data || []);
      setCategorieIngredienti(catIngRes.data || []);
      setRicette(ricRes.data || []);
      setMateriali(matRes.data || []);
    } catch (error) {
      console.error("Errore fetch:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  }, [localeCorrente?.id, getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportIngredients = async () => {
    if (!localeCorrente?.id || exportingIngredients) return;
    setExportingIngredients(true);
    try {
      const response = await fetch(
        `${API}/menu/esporta-ingredienti.xlsx?locale_id=${localeCorrente.id}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        toast.error(result.detail || "Errore durante la generazione Excel");
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      const disposition = response.headers.get("Content-Disposition") || "";
      const serverFilename = disposition.match(/filename="?([^";]+)"?/i)?.[1];
      const italianDate = new Intl.DateTimeFormat("it-IT", {
        timeZone: "Europe/Rome", day: "2-digit", month: "2-digit", year: "numeric"
      }).format(new Date()).replace(/\//g, "-");
      anchor.download = serverFilename || `Ingredienti_Menu_${(localeCorrente.nome || "menu").replace(/\s+/g, "_")}_${italianDate}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Excel ingredienti del menu scaricato");
    } catch (error) {
      toast.error("Errore di rete durante l'esportazione");
    } finally {
      setExportingIngredients(false);
    }
  };

  // Filtra prodotti
  const prodottiFiltrati = prodotti.filter(p => {
    const matchSearch = p.nome?.toLowerCase().includes(search.toLowerCase());
    const matchCategoria = filtroCategoria === "tutte" || p.categoria_id === filtroCategoria;
    const matchStato = filtroStato === "tutti" || 
      (filtroStato === "attivi" && p.attivo) || 
      (filtroStato === "inattivi" && !p.attivo);
    return matchSearch && matchCategoria && matchStato;
  });

  // Raggruppamento per categoria (prodotti inattivi in fondo)
  const prodottiPerCategoria = {};
  prodottiFiltrati
    .sort((a, b) => {
      // Prima ordina per stato attivo (attivi prima), poi per nome
      if (a.attivo !== b.attivo) return b.attivo ? 1 : -1;
      return (a.nome || "").localeCompare(b.nome || "");
    })
    .forEach(p => {
      const catId = p.categoria_id || "senza-categoria";
      if (!prodottiPerCategoria[catId]) prodottiPerCategoria[catId] = [];
      prodottiPerCategoria[catId].push(p);
    });

  // Ricalcola il costo di un componente sul prezzo/costo ATTUALE del riferimento,
  // sempre AL LORDO IVA (esborso reale). Si usa l'aliquota SPECIFICA salvata
  // nell'anagrafica del componente; se MANCA, si applica il default standard
  // (10% food / 22% packaging), IDENTICO al backend, per non azzerare l'IVA.
  // - ingrediente: prezzo_attuale netto × (1 + IVA%)
  // - packaging: prezzo_unitario netto × (1 + IVA%)
  // - ricetta: costo_per_kg (già lordo IVA alla fonte)
  const enrichComponente = (comp) => {
    const quantita = parseFloat(comp.quantita) || 0;
    if (comp.tipo === "ingrediente") {
      const ing = ingredienti.find(i => i.id === comp.riferimento_id);
      if (!ing) return comp;
      const iva = ing.iva_percentuale != null ? Number(ing.iva_percentuale) : 10;
      const prezzoKgLordo = (ing.prezzo_attuale || 0) * (1 + iva / 100);
      const isPz = comp.unita_misura === "pz";
      const costoTotale = isPz ? quantita * prezzoKgLordo : (quantita / 1000) * prezzoKgLordo;
      return { ...comp, costo_unitario: prezzoKgLordo, prezzo_netto: (ing.prezzo_attuale || 0), iva_percentuale: iva, costo_totale: Math.round(costoTotale * 100) / 100 };
    }
    if (comp.tipo === "ricetta") {
      // Fonte di verità: costo_unitario LIVE calcolato dal backend (_enrich_menu_live_costs),
      // lo stesso usato per Food Cost/Totale della card → card e dettaglio sempre identici.
      // Fallback: costo_per_kg salvato della ricetta.
      const ric = ricette.find(r => r.id === comp.riferimento_id);
      const liveKg = parseFloat(comp.costo_unitario) || 0;
      const costoPerKg = liveKg > 0 ? liveKg : (ric?.costo_per_kg || 0); // già lordo IVA
      return { ...comp, costo_unitario: costoPerKg, prezzo_netto: null, iva_percentuale: null, costo_totale: Math.round((quantita / 1000) * costoPerKg * 100) / 100 };
    }
    if (comp.tipo === "packaging") {
      const mat = materiali.find(m => m.id === comp.riferimento_id);
      if (!mat) return comp;
      const iva = mat.iva_percentuale != null ? Number(mat.iva_percentuale) : 22;
      const prezzoNetto = mat.prezzo_unitario || mat.prezzo_ultimo || mat.prezzo_migliore || 0;
      const prezzoLordo = prezzoNetto * (1 + iva / 100);
      return { ...comp, costo_unitario: prezzoLordo, prezzo_netto: prezzoNetto, iva_percentuale: iva, costo_totale: Math.round(quantita * prezzoLordo * 100) / 100 };
    }
    return comp;
  };

  // Apri dialog
  const openDialog = (item = null) => {
    if (item) {
      setEditItem(item);
      setForm({
        nome: item.nome || "",
        categoria_id: item.categoria_id || "",
        descrizione: item.descrizione || "",
        prezzo_vendita: item.prezzo_vendita?.toString() || "",
        componenti: (item.componenti || []).map(enrichComponente),
        attivo: item.attivo !== false
      });
    } else {
      setEditItem(null);
      setForm({
        nome: "",
        categoria_id: "",
        descrizione: "",
        prezzo_vendita: "",
        componenti: [],
        attivo: true
      });
    }
    setDialogOpen(true);
  };

  // Deep-link dagli alert: /menu?open={id} apre e scrolla al prodotto
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || !prodotti.length) return;
    const item = prodotti.find((p) => p.id === openId);
    if (!item) return;
    openDialog(item);
    setTimeout(() => {
      document.querySelector(`[data-testid="prodotto-card-${openId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prodotti, searchParams]);

  // Food cost TOTALE (cibo + packaging) — i costo_totale dei componenti sono già
  // AL LORDO IVA (vedi enrichComponente/handleAddComponente), quindi si sommano.
  const calcolaFoodCost = () => {
    return form.componenti.reduce((sum, c) => sum + (c.costo_totale || 0), 0);
  };

  // Food cost del solo CIBO (esclude il packaging) — coerente col badge "Food Cost" della card.
  const calcolaFoodCostCibo = () => {
    return form.componenti.reduce((sum, c) => c.tipo === "packaging" ? sum : sum + (c.costo_totale || 0), 0);
  };

  // Calcola margine
  const calcolaMargine = () => {
    const prezzoVendita = parseFloat(form.prezzo_vendita) || 0;
    const foodCost = calcolaFoodCost();
    if (prezzoVendita <= 0) return 0;
    return ((prezzoVendita - foodCost) / prezzoVendita) * 100;
  };

  // Aggiungi componente
  const handleAddComponente = () => {
    let componente = null;
    
    if (tipoComponente === "ingrediente") {
      if (!nuovoComponente.ingrediente_id) {
        toast.error("Seleziona un ingrediente");
        return;
      }
      if (!nuovoComponente.quantita || parseFloat(nuovoComponente.quantita) <= 0) {
        toast.error("Inserisci una quantità valida");
        return;
      }
      
      const ing = ingredienti.find(i => i.id === nuovoComponente.ingrediente_id);
      if (!ing) return;
      
      const quantita = parseFloat(nuovoComponente.quantita);
      const unitaMisura = ing.unita_misura || "kg";
      const iva = ing.iva_percentuale != null ? Number(ing.iva_percentuale) : 10;
      const prezzoKg = (ing.prezzo_attuale || 0) * (1 + iva / 100); // lordo IVA (aliquota anagrafica)
      // Converti quantità in kg/lt per calcolo costo
      const quantitaKg = unitaMisura === "pz" ? quantita : quantita / 1000;
      const costoTotale = unitaMisura === "pz" ? quantita * prezzoKg : quantitaKg * prezzoKg;
      
      componente = {
        tipo: "ingrediente",
        riferimento_id: ing.id,
        nome: ing.nome,
        quantita: quantita,
        unita_misura: unitaMisura === "pz" ? "pz" : "g",
        costo_unitario: prezzoKg,
        costo_totale: costoTotale
      };
    } else if (tipoComponente === "ricetta") {
      if (!nuovoComponente.ricetta_id) {
        toast.error("Seleziona una ricetta");
        return;
      }
      if (!nuovoComponente.quantita || parseFloat(nuovoComponente.quantita) <= 0) {
        toast.error("Inserisci una quantità valida");
        return;
      }
      
      const ric = ricette.find(r => r.id === nuovoComponente.ricetta_id);
      if (!ric) return;
      
      const quantita = parseFloat(nuovoComponente.quantita);
      const costoPerKg = ric.costo_per_kg || 0;
      const costoTotale = (quantita / 1000) * costoPerKg;
      
      componente = {
        tipo: "ricetta",
        riferimento_id: ric.id,
        nome: ric.nome,
        quantita: quantita,
        unita_misura: "g",
        costo_unitario: costoPerKg,
        costo_totale: costoTotale
      };
    } else if (tipoComponente === "packaging") {
      if (!nuovoComponente.packaging_id) {
        toast.error("Seleziona un packaging");
        return;
      }
      if (!nuovoComponente.quantita || parseFloat(nuovoComponente.quantita) <= 0) {
        toast.error("Inserisci una quantità valida");
        return;
      }
      
      const mat = materiali.find(m => m.id === nuovoComponente.packaging_id);
      if (!mat) return;
      
      const quantita = parseFloat(nuovoComponente.quantita);
      // Prezzo netto (prezzo_unitario / prezzo_ultimo / prezzo_migliore) × IVA anagrafica → lordo
      const iva = mat.iva_percentuale != null ? Number(mat.iva_percentuale) : 22;
      const prezzoNetto = mat.prezzo_unitario || mat.prezzo_ultimo || mat.prezzo_migliore || 0;
      const prezzoUnitario = prezzoNetto * (1 + iva / 100);
      
      if (prezzoNetto === 0) {
        toast.warning(`Il packaging "${mat.nome}" non ha un prezzo impostato. Registra un acquisto per impostare il prezzo.`);
      }
      
      componente = {
        tipo: "packaging",
        riferimento_id: mat.id,
        nome: mat.nome,
        quantita: quantita,
        unita_misura: "pz",
        costo_unitario: prezzoUnitario,
        costo_totale: quantita * prezzoUnitario
      };
    }
    
    if (componente) {
      setForm(prev => ({
        ...prev,
        componenti: [...prev.componenti, componente]
      }));
      setNuovoComponente({ ingrediente_id: "", ricetta_id: "", packaging_id: "", quantita: "" });
      setAddComponentOpen(false);
    }
  };

  // Rimuovi componente
  const removeComponente = (index) => {
    setForm(prev => ({
      ...prev,
      componenti: prev.componenti.filter((_, i) => i !== index)
    }));
  };

  // Aggiorna quantità componente
  const updateComponenteQuantita = (index, value) => {
    setForm(prev => {
      const updated = [...prev.componenti];
      const comp = updated[index];
      const quantita = parseFloat(value) || 0;
      
      // Ricalcola costo
      let costoTotale = 0;
      if (comp.tipo === "ingrediente") {
        if (comp.unita_misura === "pz") {
          costoTotale = quantita * (comp.costo_unitario || 0);
        } else {
          costoTotale = (quantita / 1000) * (comp.costo_unitario || 0);
        }
      } else if (comp.tipo === "ricetta") {
        costoTotale = (quantita / 1000) * (comp.costo_unitario || 0);
      } else if (comp.tipo === "packaging") {
        costoTotale = quantita * (comp.costo_unitario || 0);
      }
      
      updated[index] = { ...comp, quantita, costo_totale: costoTotale };
      return { ...prev, componenti: updated };
    });
  };

  // Salva prodotto
  const handleSave = async () => {
    if (!form.nome.trim()) {
      toast.error("Inserisci il nome del prodotto");
      return;
    }
    
    setSaving(true);
    try {
      const headers = getAuthHeaders();
      const payload = {
        locale_id: localeCorrente.id,
        nome: form.nome.trim(),
        categoria_id: form.categoria_id || null,
        descrizione: form.descrizione?.trim() || null,
        prezzo_vendita: parseFloat(form.prezzo_vendita) || 0,
        componenti: form.componenti,
        attivo: form.attivo
      };
      
      if (editItem) {
        await axios.put(`${API}/menu/prodotti/${editItem.id}`, payload, { headers });
        toast.success("Prodotto aggiornato");
      } else {
        await axios.post(`${API}/menu/prodotti`, payload, { headers });
        toast.success("Prodotto creato");
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Errore salvataggio:", error);
      toast.error(error.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  // Elimina prodotto
  const handleDelete = async (item) => {
    if (!await confirm(`Eliminare "${item.nome}"?`)) return;
    
    try {
      await axios.delete(`${API}/menu/prodotti/${item.id}`, { headers: getAuthHeaders() });
      toast.success("Prodotto eliminato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Toggle stato attivo/inattivo
  const handleToggleAttivo = async (item, nuovoStato) => {
    try {
      await axios.put(`${API}/menu/prodotti/${item.id}`, {
        ...item,
        attivo: nuovoStato
      }, { headers: getAuthHeaders() });
      toast.success(nuovoStato ? "Prodotto attivato" : "Prodotto disattivato");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  // Salva categoria
  const handleSaveCategoria = async () => {
    if (!nuovaCategoria.nome.trim()) {
      toast.error("Inserisci il nome della categoria");
      return;
    }
    
    try {
      await axios.post(`${API}/menu/categorie`, {
        locale_id: localeCorrente.id,
        nome: nuovaCategoria.nome.trim(),
        icona: nuovaCategoria.icona,
        colore: nuovaCategoria.colore
      }, { headers: getAuthHeaders() });
      
      toast.success("Categoria creata");
      setCategoriaDialogOpen(false);
      setNuovaCategoria({ nome: "", icona: "🍽️", colore: "#6C63FF" });
      fetchData();
    } catch (error) {
      toast.error("Errore nella creazione della categoria");
    }
  };

  // Elimina categoria
  const handleDeleteCategoria = async (catId) => {
    if (!await confirm("Eliminare questa categoria?")) return;
    
    try {
      await axios.delete(`${API}/menu/categorie/${catId}`, { headers: getAuthHeaders() });
      toast.success("Categoria eliminata");
      fetchData();
    } catch (error) {
      toast.error("Errore nell'eliminazione");
    }
  };

  // Ottieni unità display per componente
  const getUnitaDisplay = (comp) => {
    if (comp.tipo === "packaging") return "pz";
    if (comp.tipo === "ricetta") return "g";
    if (comp.tipo === "ingrediente") {
      return comp.unita_misura === "pz" ? "pz" : "g";
    }
    return comp.unita_misura || "g";
  };

  // Statistiche
  const stats = {
    totale: prodotti.length,
    attivi: prodotti.filter(p => p.attivo).length,
    margineMediano: prodotti.length > 0 
      ? (prodotti.reduce((sum, p) => sum + (p.margine_percentuale || 0), 0) / prodotti.length).toFixed(1)
      : 0,
    foodCostMedio: prodotti.length > 0
      ? (prodotti.reduce((sum, p) => sum + ((p.totale_live ?? p.food_cost_live ?? p.food_cost) || 0), 0) / prodotti.length).toFixed(2)
      : 0
  };

  // Top 3 piatti per REDDITIVITÀ (margine % più alto, attivi, margine > 0)
  // Niente stime vendita: usiamo solo il margine % della ricetta — dato oggettivo già calcolato.
  const top3Margine = prodotti
    .filter(p => p.attivo && (parseFloat(p.margine_percentuale) || 0) > 0 && (parseFloat(p.prezzo_vendita) || 0) > 0)
    .map(p => ({ ...p, _margine: parseFloat(p.margine_percentuale) || 0 }))
    .sort((a, b) => b._margine - a._margine)
    .slice(0, 3);
  const topMargineMap = Object.fromEntries(top3Margine.map((p, idx) => [p.id, idx + 1]));
  // crownRank ora in menuHelpers

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="menu-loading-state">
        <div className="animate-pulse text-gray-500">Caricamento...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 md:pb-6" data-testid="menu-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <UtensilsCrossed className="w-6 h-6 text-[#FF6584]" />
            Menu di vendita
          </h1>
          <p className="text-sm text-gray-500 mt-1 hidden sm:block">
            Gestisci i piatti del tuo menu con calcolo automatico del food cost
          </p>
        </div>
        <div className="flex gap-2 flex-wrap items-center sm:justify-end">
          <Button
            variant="outline"
            size="sm"
            disabled={exportingIngredients}
            onClick={handleExportIngredients}
            className="gap-2 h-10"
            data-testid="export-menu-ingredients-excel-btn"
          >
            <FileSpreadsheet className={`h-4 w-4 text-emerald-600 ${exportingIngredients ? "animate-pulse" : ""}`} />
            <span className="hidden sm:inline">
              {exportingIngredients ? "Genero Excel..." : "Esporta Ingredienti Menù"}
            </span>
            <span className="sm:hidden">Ingredienti</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={exportingFoodCost}
            onClick={async () => {
              if (!localeCorrente?.id) return;
              setExportingFoodCost(true);
              try {
                const res = await fetch(
                  `${API}/menu/food-cost/pdf?locale_id=${localeCorrente.id}`,
                  { headers: getAuthHeaders() }
                );
                if (!res.ok) { toast.error("Errore generazione PDF"); return; }
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `food_cost_${(localeCorrente?.nome || "menu").replace(/\s/g, "_")}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("📄 PDF food cost scaricato");
              } catch (e) {
                toast.error("Errore di rete");
              } finally {
                setExportingFoodCost(false);
              }
            }}
            className="gap-2 h-10"
            data-testid="export-food-cost-pdf-btn"
          >
            <FileSpreadsheet className={`w-4 h-4 ${exportingFoodCost ? "animate-pulse" : ""}`} />
            <span className="hidden sm:inline">{exportingFoodCost ? "Genero PDF..." : "PDF Food Cost"}</span>
            <span className="sm:hidden">Food Cost</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!localeCorrente?.id) return;
              try {
                const res = await fetch(
                  `${API}/menu/allergeni/pdf?locale_id=${localeCorrente.id}`,
                  { headers: getAuthHeaders() }
                );
                if (!res.ok) { toast.error("Errore generazione PDF"); return; }
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `allergeni_${(localeCorrente?.nome || "menu").replace(/\s/g, "_")}.pdf`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("📄 PDF allergeni scaricato");
              } catch (e) {
                toast.error("Errore di rete");
              }
            }}
            className="gap-2 h-10"
            data-testid="export-allergeni-pdf-btn"
          >
            <FileDown className="w-4 h-4" />
            <span className="hidden sm:inline">PDF Allergeni</span>
            <span className="sm:hidden">Allergeni</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCategoriaDialogOpen(true)}
            className="gap-2 h-10"
            data-testid="nuova-categoria-btn"
          >
            <Tag className="w-4 h-4" />
            <span className="hidden sm:inline">Categorie</span>
          </Button>
          <Button
            onClick={() => openDialog()}
            className="gap-2 bg-[#FF6584] hover:bg-[#E85570] hidden sm:inline-flex"
            data-testid="nuovo-prodotto-btn"
          >
            <Plus className="w-4 h-4" />
            Nuovo Prodotto
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <MenuStatsCards stats={stats} />

      {/* Top 3 Piatti per redditività */}
      <MenuTop3Card top3={top3Margine} categorie={categorie} onSelect={openDialog} />

      {/* Filtri */}
      <MenuFilters
        search={search}
        onSearchChange={setSearch}
        filtroCategoria={filtroCategoria}
        onFiltroCategoriaChange={setFiltroCategoria}
        filtroStato={filtroStato}
        onFiltroStatoChange={setFiltroStato}
        categorie={categorie}
      />

      {/* Lista prodotti per categoria */}
      {prodottiFiltrati.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="py-12 text-center">
            <UtensilsCrossed className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Nessun prodotto trovato</p>
            <Button 
              onClick={() => openDialog()} 
              className="mt-4 bg-[#FF6584] hover:bg-[#E85570]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Crea il primo prodotto
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(prodottiPerCategoria).map(([catId, prods]) => {
            const categoria = categorie.find(c => c.id === catId);
            return (
              <div key={catId}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-lg">{categoria?.icona || "📦"}</span>
                  <h2 className="font-semibold text-gray-800">
                    {categoria?.nome || "Senza categoria"}
                  </h2>
                  <Badge variant="secondary" className="ml-2">{prods.length}</Badge>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {prods.map((prod) => {
                    const categoriaProd = categorie.find((c) => c.id === prod.categoria_id);
                    const rank = topMargineMap[prod.id];
                    return (
                      <MenuProdottoCard
                        key={prod.id}
                        prod={prod}
                        categoria={categoriaProd}
                        rank={rank}
                        onOpen={openDialog}
                        onDelete={handleDelete}
                        onToggleAttivo={handleToggleAttivo}
                        alert={alertMap[prod.id]}
                        onOpenStorico={(p) => setStoricoItem(p)}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog Prodotto */}
      <MenuProdottoDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editItem={editItem}
        form={form}
        setForm={setForm}
        onSave={handleSave}
        saving={saving}
        categorie={categorie}
        ingredienti={ingredienti}
        categorieIngredienti={categorieIngredienti}
        ricette={ricette}
        materiali={materiali}
        addComponentOpen={addComponentOpen}
        setAddComponentOpen={setAddComponentOpen}
        tipoComponente={tipoComponente}
        setTipoComponente={setTipoComponente}
        nuovoComponente={nuovoComponente}
        setNuovoComponente={setNuovoComponente}
        onAddComponente={handleAddComponente}
        removeComponente={removeComponente}
        updateComponenteQuantita={updateComponenteQuantita}
        getUnitaDisplay={getUnitaDisplay}
        calcolaFoodCost={calcolaFoodCost}
        calcolaFoodCostCibo={calcolaFoodCostCibo}
        calcolaMargine={calcolaMargine}
      />

      {/* Dialog Aggiungi Componente */}
      <MenuComponenteDialog
        open={addComponentOpen}
        onOpenChange={setAddComponentOpen}
        tipoComponente={tipoComponente}
        setTipoComponente={setTipoComponente}
        nuovoComponente={nuovoComponente}
        setNuovoComponente={setNuovoComponente}
        onAddComponente={handleAddComponente}
        ingredienti={ingredienti}
        categorieIngredienti={categorieIngredienti}
        ricette={ricette}
        materiali={materiali}
      />

      {/* Dialog Categoria */}
      <MenuCategoriaDialog
        open={categoriaDialogOpen}
        onOpenChange={setCategoriaDialogOpen}
        categorie={categorie}
        nuovaCategoria={nuovaCategoria}
        setNuovaCategoria={setNuovaCategoria}
        onSave={handleSaveCategoria}
        onDeleteCategoria={handleDeleteCategoria}
      />

      {/* FAB mobile: Nuovo prodotto */}
      <Button
        onClick={() => openDialog()}
        size="icon"
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-xl bg-[#FF6584] hover:bg-[#E85570] z-40 md:hidden"
        aria-label="Nuovo prodotto"
        data-testid="fab-nuovo-prodotto"
      >
        <Plus className="h-6 w-6 text-white" />
      </Button>

      <StoricoVariazioniDialog
        open={!!storicoItem}
        onOpenChange={(o) => { if (!o) setStoricoItem(null); }}
        itemId={storicoItem?.id}
        itemNome={storicoItem?.nome}
        tipo="menu"
        prezzoVendita={storicoItem?.prezzo_vendita || 0}
      />
    </div>
  );
};

export default Menu;
